import React, { cloneElement, useContext, useEffect, useState } from 'react';

import { DropdownMenuContext } from '../jdsdropdownmenu/context';
import { type DropdownMenuItemProps } from '../jdsdropdownmenu/interface';
import { getIcon } from '../jdsdropdownmenu/logic';
import { getUniqueId } from '../jdsdropdownmenu/utils/dropdownmenu-util';
import { handlePrevOrNextItemFocus } from '../jdsdropdownmenu/utils/dropdownmenu-logic';
import { useRootRef } from '../../hooks/useRootRef';

const DROPDOWNMENUITEM_SELECTOR = 'div.j-JDSDropdownMenuItem-container';
const SUBMENU_OFFSET_TOP = 8;
const SUBMENU_MARGIN = 4; // xxs

const getPrefixComponent = (
  prefix?: string | JSX.Element
): JSX.Element | undefined => {
  // icon
  if (typeof prefix === 'string') {
    return getIcon(prefix);
  }

  // image
  if (prefix?.props?.src) {
    return cloneElement(prefix, {
      ...prefix.props,
      roundedCorner: true,
      aspectRatio: '1:1',
      style: { width: '24px', height: '24px' }
    });
  }

  // avatar
  if (prefix?.props?.initials || prefix?.props?.kind) {
    return cloneElement(prefix, {
      ...prefix.props,
      size: 'x-small'
    });
  }

  return undefined;
};

export const withLogic =
  (
    Component: React.ComponentType<DropdownMenuItemProps>
  ): React.FC<DropdownMenuItemProps> =>
  (props: DropdownMenuItemProps) => {
    const { element, rootRef } = useRootRef();

    const [selected, setSelected] = useState(false);

    const [menuItemId, setMenuItemId] = useState('');

    const context = useContext(DropdownMenuContext);

    const [offset, setOffset] = useState<{
      offsetX: string | number;
      offsetY: string | number;
    }>({
      offsetX: 0,
      offsetY: 0
    });

    useEffect(() => {
      setMenuItemId(getUniqueId());
    }, []);

    useEffect(() => {
      // if it is nested menu then do maintain the selected state
      if (context.isNested && !props.children) {
        if (selected) {
          setSelected(false);
        }
        return;
      }
      if (props.children) {
        context.setIsNested(true);
        setSelected(context.activeMenuId === menuItemId);
      } else {
        if (props.kind === 'multi-select') {
          setSelected(context.selectedValues.includes(props.value || ''));
        } else {
          setSelected(
            context.selectedValue !== undefined &&
              context.selectedValue === props.value
          );
        }
      }
    }, [
      selected,
      context.activeMenuId,
      menuItemId,
      context.selectedValue,
      context.selectedValues,
      context.isNested,
      props.value,
      props.children
    ]);

    const handleSubMenuPositioning = () => {
      const dropdownMenuItemRef = element?.querySelector<HTMLDivElement>(
        DROPDOWNMENUITEM_SELECTOR
      );

      if (dropdownMenuItemRef) {
        const { right, top, bottom, left } =
          dropdownMenuItemRef.getBoundingClientRect();

        setOffset({
          offsetX: `${right + SUBMENU_MARGIN}px`,
          offsetY: `${top - SUBMENU_OFFSET_TOP}px`
        });

        setTimeout(() => {
          const subMenuRef = document.querySelector(
            '.j-jds-JDSDropdownMenuSubMenu-menu-container'
          );

          if (!subMenuRef) {
            return;
          }

          const subMenuDomRect = subMenuRef.getBoundingClientRect();

          if (subMenuDomRect.right < window.innerWidth) {
            return;
          }

          if (subMenuDomRect.width < left) {
            setOffset(({ offsetY }) => ({
              offsetX: `${left - (subMenuDomRect.width + SUBMENU_MARGIN)}px`,
              offsetY
            }));
          } else {
            setOffset({
              offsetX: `${window.innerWidth - subMenuDomRect.width}px`,
              offsetY: `${bottom + SUBMENU_MARGIN}px`
            });
          }
        }, 50);
      }
    };

    const dropdownMenuItemRef = element?.querySelector<HTMLDivElement>(
      DROPDOWNMENUITEM_SELECTOR
    );

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        switch (event.key) {
          case 'ArrowRight': {
            if (document.activeElement === dropdownMenuItemRef) {
              if (Boolean(props.children) && !selected) {
                context.setActiveMenuId(menuItemId);
                handleSubMenuPositioning();
                setTimeout(() => {
                  const subMenuRef = document.querySelector(
                    '.j-JDSDropdownMenuSubMenu-menu-container'
                  ) as HTMLElement;
                  if (subMenuRef) {
                    handlePrevOrNextItemFocus(
                      1,
                      subMenuRef,
                      '.j-JDSDropdownMenuItem-container'
                    );
                  }
                }, 10);
              }
              event.preventDefault();
              event.stopPropagation();
            }
            break;
          }
          case 'ArrowLeft':
          case 'Escape': {
            if (selected && context.activeMenuId) {
              setSelected(false);
              context.setActiveMenuId('');
              dropdownMenuItemRef?.focus?.();
            }
            break;
          }
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [context.activeMenuId, selected, dropdownMenuItemRef, menuItemId]);

    const children = React.Children.toArray(props.children?.props.children);
    const selectedItems = React.Children.toArray(
      props.children?.props.children
    ).filter((child) => {
      if (!(typeof child === 'object')) return false;
      if (!('props' in child)) return false;
      return child.props.selected;
    });
    const isSelected = children.length
      ? selectedItems.length === children.length
      : props.selected;
    const handleClick = (event: React.MouseEvent) => {
      const updatedSelectedValues = [...context.selectedValues];

      if (props.children) {
        context.setActiveMenuId(menuItemId);
        handleSubMenuPositioning();
      } else {
        if (props.kind === 'multi-select') {
          const isChecked = (event.target as HTMLInputElement).checked;

          if (isChecked) {
            if (!updatedSelectedValues.includes(props.value ?? '')) {
              updatedSelectedValues.push(props.value ?? '');
            }
          } else {
            const index = updatedSelectedValues.indexOf(props.value ?? '');
            if (index > -1) {
              updatedSelectedValues.splice(index, 1);
            }
          }
        } else {
          context.setSelectedValue(props.value ?? '');
        }
      }

      // Handle selection for children
      if (props.kind === 'multi-select') {
        children.forEach((child) => {
          if (typeof child === 'object' && 'props' in child) {
            if (child.props.selected) {
              if (!updatedSelectedValues.includes(child.props.value)) {
                updatedSelectedValues.push(child.props.value);
              }
            } else {
              const index = updatedSelectedValues.indexOf(child.props.value);
              if (index > -1) {
                updatedSelectedValues.splice(index, 1);
              }
            }
          }
        });

        context.setSelectedValues(updatedSelectedValues);
      }

      props?.onClick?.(props.value);
    };
    return (
      <Component
        {...{
          ...props,
          rootRef,
          prefix: getPrefixComponent(props.prefix),
          disabled: Boolean(props.disabled),
          _labelTextWrap: context.labelTextWrap,
          _offsetX: offset.offsetX,
          _offsetY: offset.offsetY,
          _hasSubMenu: Boolean(props.children),
          selected,
          _checked: isSelected,
          _indeterminate: !isSelected && selectedItems.length > 0,
          className: selected ? 'selected' : '',
          onClick: handleClick
        }}
      />
    );
  };
export { DropdownMenuItemProps };
